import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatStatus } from "ai";
import { useTranslation } from "react-i18next";
import { uploadAttachmentRefsToServer } from "@/hooks/use-agno-chat";

type QueuedAttachment = { path: string; filename: string; mime?: string };

export type PromptQueueItem = {
  id: string;
  text: string;
  attachments?: QueuedAttachment[];
  delivery_error?: string | null;
  client_mutation_id?: string;
  claimed: boolean;
  stale?: boolean;
  delivered_run_id?: string;
};

type QueueFetch = (url: string, init: RequestInit) => Promise<Response>;

export function usePromptQueue({ sessionId, agentId, status, messages, fetchWithAuth, bootstrapQueue, bootstrapPending = false }: {
  sessionId: string;
  agentId?: string | null;
  status: ChatStatus;
  messages: readonly { metadata?: { runId?: string } }[];
  fetchWithAuth: QueueFetch;
  bootstrapQueue?: { items: PromptQueueItem[]; supports_attachments: boolean } | null;
  bootstrapPending?: boolean;
}) {
  const [promptQueue, setPromptQueue] = useState<PromptQueueItem[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [editingPromptText, setEditingPromptText] = useState("");
  const [promptQueueSupported, setPromptQueueSupported] = useState<boolean | null>(null);
  const [attachmentsSupported, setAttachmentsSupported] = useState(false);
  const [promptResendSupported, setPromptResendSupported] = useState(true);
  const [promptResendError, setPromptResendError] = useState<string | null>(null);
  const [promptDeleteError, setPromptDeleteError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [resendingPromptId, setResendingPromptId] = useState<string | null>(null);
  const { t } = useTranslation("chat");
  const bootstrapInitialRef = useRef<{ sessionId: string; status: ChatStatus; tick: number } | null>(null);

  useEffect(() => {
    setPromptResendSupported(true);
    setPromptResendError(null);
    setPromptDeleteError(null);
    setSubmitError(null);
  }, [sessionId]);

  useEffect(() => {
    if (status === "ready") setSubmitError(null);
  }, [status]);

  useEffect(() => {
    if (promptQueueSupported === false) return;
    if (bootstrapPending) return;
    if (bootstrapQueue && (!bootstrapInitialRef.current || bootstrapInitialRef.current.sessionId !== sessionId)) {
      bootstrapInitialRef.current = { sessionId, status, tick: refreshTick };
      setPromptQueue(bootstrapQueue.items);
      setPromptQueueSupported(true);
      setAttachmentsSupported(bootstrapQueue.supports_attachments);
      return;
    }
    if (bootstrapQueue && bootstrapInitialRef.current?.sessionId === sessionId
        && bootstrapInitialRef.current.status === status && bootstrapInitialRef.current.tick === refreshTick) return;
    let cancelled = false;
    void fetchWithAuth(`/api/sessions/${encodeURIComponent(sessionId)}/prompt-queue`, { headers: {} })
      .then(async (response) => {
        if (response.status === 404) { setPromptQueueSupported(false); return; }
        if (!response.ok) return;
        const snapshot = await response.json();
        if (!cancelled) {
          setPromptQueueSupported(true);
          setAttachmentsSupported(snapshot.supports_attachments === true);
        }
        if (!cancelled) setPromptQueue((local) => {
          const server = snapshot.items ?? [];
          const serverMutationIds = new Set(server.map((item: PromptQueueItem) => item.client_mutation_id));
          return [...local.filter((item) => item.id.startsWith("temp-") && !serverMutationIds.has(item.client_mutation_id)), ...server];
        });
      }).catch(() => {
        // Leave support unknown after a transient fetch failure.
      });
    return () => { cancelled = true; };
  }, [fetchWithAuth, sessionId, status, refreshTick, bootstrapQueue, bootstrapPending]);

  // A queued prompt may start and finish without this tab's chat status
  // changing. Recheck while chips are visible so a terminal server response
  // removes them without requiring navigation or a history reload.
  useEffect(() => {
    if (promptQueue.length === 0 || promptQueueSupported === false) return;
    const timer = window.setInterval(() => setRefreshTick((tick) => tick + 1), 5000);
    return () => window.clearInterval(timer);
  }, [promptQueue.length, promptQueueSupported]);

  useEffect(() => {
    setPromptQueue((items) => {
      if (items.length === 0) return items;
      const remaining = items.filter(
        (item) => !item.delivered_run_id
          || !messages.some((message) => message.metadata?.runId === item.delivered_run_id),
      );
      return remaining.length === items.length ? items : remaining;
    });
  // A queue refresh can add a delivered row after its message is already
  // loaded. Reconcile on queue changes too, or that row remains "Running".
  }, [messages, promptQueue]);

  const editQueuedPrompt = useCallback(async (item: PromptQueueItem, text: string) => {
    const response = await fetchWithAuth(`/api/sessions/${encodeURIComponent(sessionId)}/prompt-queue/${item.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
    if (response.status === 409) { setPromptQueue((items) => items.map((entry) => entry.id === item.id ? { ...entry, claimed: true } : entry)); return; }
    if (response.ok) { const updated = await response.json(); setPromptQueue((items) => items.map((entry) => entry.id === item.id ? updated : entry)); }
  }, [fetchWithAuth, sessionId]);

  const deleteQueuedPrompt = useCallback(async (item: PromptQueueItem) => {
    setPromptDeleteError(null);
    try {
      const response = await fetchWithAuth(`/api/sessions/${encodeURIComponent(sessionId)}/prompt-queue/${encodeURIComponent(item.id)}`, { method: "DELETE", headers: {} });
      if (response.status === 404 || response.status === 405) {
        setPromptDeleteError(t("input.promptQueue.deleteUnavailable"));
      } else if (response.status === 409) {
        setPromptDeleteError(t("input.promptQueue.deleteConflict"));
      } else if (response.ok) {
        setPromptQueue((items) => items.filter((entry) => entry.id !== item.id));
      } else {
        setPromptDeleteError(t("input.promptQueue.deleteFailed"));
      }
    } catch (error) {
      console.error("[ui] Failed to delete queued prompt:", error);
      setPromptDeleteError(t("input.promptQueue.deleteFailed"));
    }
  }, [fetchWithAuth, sessionId, t]);

  const resendQueuedPrompt = useCallback(async (item: PromptQueueItem) => {
    setResendingPromptId(item.id);
    setPromptResendError(null);
    try {
      const response = await fetchWithAuth(`/api/sessions/${encodeURIComponent(sessionId)}/prompt-queue/${encodeURIComponent(item.id)}/re-enqueue`, { method: "POST", headers: {} });
      if (response.status === 404) {
        setPromptResendSupported(false);
        setPromptResendError(t("input.promptQueue.resendUnavailable"));
        return;
      }
      if (!response.ok) throw new Error(`Prompt resend HTTP ${response.status}`);
      const fresh: PromptQueueItem = await response.json();
      setPromptQueue((items) => items.map((entry) => entry.id === item.id ? fresh : entry));
    } catch (error) {
      console.error("[ui] Failed to resend stale prompt:", error);
      setPromptResendError(t("input.promptQueue.resendFailed"));
    } finally {
      setResendingPromptId(null);
    }
  }, [fetchWithAuth, sessionId, t]);

  const queueIfBusy = useCallback(async (text: string, files: readonly unknown[]) => {
    if (status !== "streaming" && status !== "submitted") return false;
    if (files.length > 0 && !attachmentsSupported) {
      const message = t("input.promptQueue.attachmentsWhileBusy");
      setSubmitError(message);
      throw new Error(message);
    }
    setSubmitError(null);
    if (promptQueueSupported === false) throw new Error("Prompt queue is unavailable during an active run");
    let attachments: QueuedAttachment[] = [];
    if (files.length > 0) {
      try {
        const uploaded = await uploadAttachmentRefsToServer(sessionId, agentId, [...files], fetchWithAuth);
        if (uploaded.length !== files.length) throw new Error("Incomplete attachment upload");
        attachments = uploaded.map(({ path, filename, mime_type }) => ({ path, filename, mime: mime_type }));
      } catch (error) {
        const message = t("input.promptQueue.uploadFailed");
        setSubmitError(message);
        throw new Error(message, { cause: error });
      }
    }
    const mutationId = crypto.randomUUID();
    const optimistic = { id: `temp-${mutationId}`, text, attachments, client_mutation_id: mutationId, claimed: false };
    setPromptQueue((items) => [...items, optimistic]);
    let response: Response;
    try {
      response = await fetchWithAuth(`/api/sessions/${encodeURIComponent(sessionId)}/prompt-queue`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, client_mutation_id: mutationId, ...(attachments.length ? { attachments } : {}) }),
      });
    } catch (error) {
      setPromptQueue((items) => items.filter((item) => item.id !== optimistic.id));
      const message = t("input.promptQueue.enqueueFailed");
      setSubmitError(message);
      throw new Error(message, { cause: error });
    }
    if (response.status === 404) {
      setPromptQueue((items) => items.filter((item) => item.id !== optimistic.id));
      setPromptQueueSupported(false);
      if (attachments.length) { const message = t("input.promptQueue.enqueueFailed"); setSubmitError(message); throw new Error(message); }
      throw new Error("Prompt queue is unavailable during an active run");
    }
    if (!response.ok) { setPromptQueue((items) => items.filter((item) => item.id !== optimistic.id)); const message = t("input.promptQueue.enqueueFailed"); setSubmitError(message); throw new Error(message); }
    const stored = await response.json();
    setPromptQueueSupported(true);
    setPromptQueue((items) => items.map((item) => item.id === optimistic.id ? stored : item));
    return true;
  }, [agentId, attachmentsSupported, fetchWithAuth, promptQueueSupported, sessionId, status, t]);

  return {
    promptQueue, submitError, promptDeleteError, editingPromptId, setEditingPromptId, editingPromptText, setEditingPromptText,
    promptQueueSupported, editQueuedPrompt, deleteQueuedPrompt, queueIfBusy,
    submitDisabled: (status === "submitted" || status === "streaming") && promptQueueSupported === false,
    promptResendSupported, promptResendError, resendingPromptId, resendQueuedPrompt,
  };
}
